import React, { useEffect, useState } from "react";
import { Pause, Play, X, ShieldCheck, Download } from "lucide-react";
import { generateFakeFilename, formatSize, calcFakeSize, pickFakeBitrate, fakePeerCount, randomPeer } from "@/lib/fakeData";

/**
 * Fake "currently downloading" list for the Downloads tab.
 * Uses the user's queue + a couple of fake static entries for visual interest.
 */
const FAKE_FIXED = [
  {
    filename: "Backstreet_Boys-_I_Want_It_That_Way_[REAL]_320kbps.mp3",
    artist: "Backstreet Boys",
    size: 8421, bitrate: 320, peer: "xXxRaVeKiNg2003xXx",
    progress: 0.78, speed: 142, eta: "0:12",
  },
  {
    filename: "S-Club_7-_Reach_[LIVE_AT_TOTP]_192kbps.mp3",
    artist: "S Club 7",
    size: 4120, bitrate: 192, peer: "napsteRnightmare",
    progress: 0.34, speed: 89, eta: "0:42",
  },
  {
    filename: "Crazy_Frog-_Axel_F_REAL_NOT_VIRUS.mp3",
    artist: "Crazy Frog",
    size: 3280, bitrate: 128, peer: "dial_up_dylan",
    progress: 0.95, speed: 38, eta: "0:04",
  },
];

export default function DownloadsView({ queue = [] }) {
  // Animate fake progress
  const [items, setItems] = useState(() => FAKE_FIXED.map((i) => ({ ...i })));

  useEffect(() => {
    const t = setInterval(() => {
      setItems((prev) =>
        prev.map((it) => {
          let p = it.progress + Math.random() * 0.04;
          if (p >= 1) p = 0.08; // loop
          return { ...it, progress: p };
        })
      );
    }, 900);
    return () => clearInterval(t);
  }, []);

  // Convert user-queue tracks into "completed" rows
  const completed = queue.slice(0, 8).map((t) => {
    const br = pickFakeBitrate(t);
    return {
      filename: generateFakeFilename(t),
      artist: t.artist,
      size: calcFakeSize(t, br),
      bitrate: br,
      peer: randomPeer(),
      peers: fakePeerCount(t),
      id: t.id,
    };
  });

  return (
    <div data-testid="downloads-view" className="flex flex-col flex-1 min-h-0 gap-2">
      {/* Summary */}
      <div className="flex items-center gap-2 bevel-in bg-white px-3 py-2">
        <Download size={16} className="text-[#003c9e]" />
        <span className="text-[12px] font-bold">Active downloads: {items.length}</span>
        <span className="mx-1 text-[#888]">·</span>
        <span className="text-[11px] text-[#555]">Completed: <b>{completed.length}</b></span>
        <span className="mx-1 text-[#888]">·</span>
        <span className="text-[11px] text-[#1F8020]">
          Aggregate speed: <b>{items.reduce((s, i) => s + i.speed, 0)} KB/s</b>
        </span>
        <div className="ml-auto flex gap-1">
          <button className="bevel-btn text-[11px] px-2 py-0.5">Pause All</button>
          <button className="bevel-btn text-[11px] px-2 py-0.5">Resume All</button>
          <button className="bevel-btn text-[11px] px-2 py-0.5">Clear</button>
        </div>
      </div>

      {/* Active downloads */}
      <div className="bevel-in bg-white flex-1 min-h-0 overflow-y-auto xp-scroll">
        <div className="flex text-[11px] font-bold bg-[#ECE9D8] border-b border-[#808080] sticky top-0">
          <Hd w={26}>#</Hd>
          <Hd grow>Filename</Hd>
          <Hd w={110}>Progress</Hd>
          <Hd w={60} align="right">Size</Hd>
          <Hd w={52} align="right">kbps</Hd>
          <Hd w={72} align="right">Speed</Hd>
          <Hd w={56} align="right">ETA</Hd>
          <Hd w={140}>Peer</Hd>
          <Hd w={50} align="center">Scan</Hd>
        </div>
        {items.map((it, i) => (
          <div
            key={i}
            data-testid={`dl-row-${i}`}
            className={`flex items-center text-[11px] border-b border-[#F0EFE7] ${i % 2 === 0 ? "bg-white" : "bg-[#F8F6EE]"}`}
          >
            <Cell w={26}>{i + 1}</Cell>
            <Cell grow className="min-w-0">
              <span className="font-mono text-[#003c9e] text-[10.5px] truncate block" title={it.filename}>
                {it.filename}
              </span>
            </Cell>
            <Cell w={110}>
              <div className="xp-progress flex-1" style={{ height: 12 }}>
                <div className="xp-progress-fill" style={{ width: `${Math.round(it.progress * 100)}%` }} />
              </div>
              <span className="ml-1 font-mono text-[10px] text-[#333] min-w-[28px]">{Math.round(it.progress * 100)}%</span>
            </Cell>
            <Cell w={60} align="right">{formatSize(it.size)}</Cell>
            <Cell w={52} align="right" className="font-mono">{it.bitrate}</Cell>
            <Cell w={72} align="right" className="font-mono text-[#1F8020]">{it.speed} KB/s</Cell>
            <Cell w={56} align="right" className="font-mono">{it.eta}</Cell>
            <Cell w={140} className="truncate text-[10px] font-mono text-[#555]">{it.peer}</Cell>
            <Cell w={50} align="center">
              <div className="text-[#1F8020] flex items-center gap-0.5">
                <ShieldCheck size={10} />
                <span className="text-[9px] font-bold">OK</span>
              </div>
            </Cell>
          </div>
        ))}

        {completed.length > 0 && (
          <>
            <div className="px-2 py-1 bg-[#ECE9D8] border-b border-t border-[#808080] text-[11px] font-bold">
              Completed ({completed.length})
            </div>
            {completed.map((c, i) => (
              <div key={c.id} className="flex items-center text-[11px] border-b border-[#F0EFE7] bg-[#F5FFF5]">
                <Cell w={26}>✓</Cell>
                <Cell grow className="min-w-0">
                  <span className="font-mono text-[#1F8020] text-[10.5px] truncate block" title={c.filename}>
                    {c.filename}
                  </span>
                </Cell>
                <Cell w={110} className="text-[#1F8020]">DONE</Cell>
                <Cell w={60} align="right">{formatSize(c.size)}</Cell>
                <Cell w={52} align="right" className="font-mono">{c.bitrate}</Cell>
                <Cell w={72} align="right" className="font-mono text-[#555]">—</Cell>
                <Cell w={56} align="right" className="font-mono">—</Cell>
                <Cell w={140} className="truncate text-[10px] font-mono text-[#555]">{c.peer}</Cell>
                <Cell w={50} align="center">
                  <div className="text-[#1F8020] flex items-center gap-0.5">
                    <ShieldCheck size={10} />
                    <span className="text-[9px] font-bold">CLEAN</span>
                  </div>
                </Cell>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function Hd({ children, w, grow, align }) {
  return (
    <div
      className="px-2 py-[3px] border-r border-[#9C9A7E]"
      style={{
        width: w, flex: grow ? 1 : undefined,
        textAlign: align || "left",
      }}
    >
      {children}
    </div>
  );
}

function Cell({ children, w, grow, align, className = "" }) {
  return (
    <div
      className={`px-2 py-[3px] flex items-center ${className}`}
      style={{
        width: w,
        flex: grow ? 1 : undefined,
        justifyContent: align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start",
        minWidth: 0,
      }}
    >
      {children}
    </div>
  );
}
